import React, { useState, useEffect, useRef } from "react";
import {
  CardHeader,
  CardContent,
  IconButton,
  Divider,
  Typography,
  Button,
  Tooltip,
  Paper,
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import { Close, Link, LinkOff, Help } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import axios from "../../../AxiosClient";
import { makeStyles } from "@material-ui/core/styles";
import DocumentAutosuggest from "../../document/DocumentAutosuggest";
import { Virtuoso } from "react-virtuoso";
import { AutoSizer } from "react-virtualized";
import { useSnackbar } from "notistack";
import ItemDocument from "../../list-item/ItemDocument";
import ActionButton from "../../ActionButton";

const useStyles = makeStyles((theme) => ({
  newContainer: {},
  btnContainer: {
    marginTop: 15,
    textAlign: "center",
  },
  divider: {
    marginTop: 10,
    marginBottom: 20,
  },
  cardContent: {
    display: "flex",
    height: "85%",
    flexDirection: "column",
  },
  relatedContainer: {
    flex: 1,
  },
  helpTooltip: {
    backgroundColor: "white",
  },
}));

function SidebarRelatedDocuments(props) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [relatedDocuments, setRelatedDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [toRelate, setToRelate] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`/document/${props.documentId}/related/`)
      .then((res) => {
        setRelatedDocuments(res.data);
        setLoading(false);
        props.countOfRelatedDocuments(res.data.length);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  useEffect(
    () => {
      props.countOfRelatedDocuments(relatedDocuments.length);
    },
    [relatedDocuments]
  );

  const onDocumentSelected = (selected) => {
    setToRelate(selected.documentId);
  };

  const renderItem = (idx) => {
    return (
      <ItemDocument
        noIcons
        data={relatedDocuments[idx]}
        preText={
          <Tooltip title={t("unlink_document_tooltip")}>
            <IconButton
              aria-label="Unlink document"
              size="small"
              onClick={removeRelatedDocument(idx)}
              style={{ marginRight: 5 }}
            >
              <LinkOff />
            </IconButton>
          </Tooltip>
        }
      />
    );
  };

  const removeRelatedDocument = (idx) => (e) => {
    e.stopPropagation();
    axios
      .delete(
        `/document/${props.documentId}/related/${
          relatedDocuments[idx].documentID
        }`
      )
      .then((res) => {
        enqueueSnackbar(t("document_removed_related_snackbar"), {
          variant: "success",
        });
        setRelatedDocuments(
          relatedDocuments.filter(
            (doc) => doc.documentID !== relatedDocuments[idx].documentID
          )
        );
      })
      .catch((err) => {
        enqueueSnackbar(
          t("workflow_error", { error: err.response.data.message }),
          {
            variant: "error",
          }
        );
      });
  };

  const relateDocument = () => {
    setSaving(true);
    axios
      .put(`/document/${props.documentId}/related/${toRelate}`)
      .then((res) => {
        enqueueSnackbar(t("document_related_snackbar"), {
          variant: "success",
        });
        setRelatedDocuments([...relatedDocuments, res.data]);
        setToRelate(0);
        setSaving(false);
      })
      .catch((err) => {
        enqueueSnackbar(
          t("workflow_error", { error: err.response.data.message }),
          {
            variant: "error",
          }
        );
        setSaving(false);
      });
  };

  return (
    <>
      {props.show && (
        <>
          <CardHeader
            titleTypographyProps={{ component: "h3" }}
            title={t("related_documents_tooltip")}
            subheader={
              <Tooltip
                classes={{ tooltip: classes.helpTooltip }}
                title={
                  <>
                    <Paper elevation={5} style={{ padding: "5px 10px" }}>
                      <Typography gutterBottom variant="subtitle1">
                        {t("related_document_help_text")}
                      </Typography>
                    </Paper>
                  </>
                }
              >
                <Help />
              </Tooltip>
            }
            action={
              <IconButton
                onClick={props.toggleSidebar}
                style={{ marginRight: 5 }}
                aria-label={"Close sidebar"}
              >
                <Close />
              </IconButton>
            }
          />
          <CardContent className={classes.cardContent}>
            <div className={classes.newContainer}>
              {toRelate === props.documentId && (
                <Typography gutterBottom color="error" variant="body2">
                  {t("related_document_error_self")}
                </Typography>
              )}
              {relatedDocuments.filter((rd) => rd.documentID === toRelate)
                .length > 0 && (
                <Typography gutterBottom color="error" variant="body2">
                  {t("related_document_error_already_linked")}
                </Typography>
              )}
              <DocumentAutosuggest onItemSelected={onDocumentSelected} />
              <div className={classes.btnContainer}>
                <ActionButton
                  variant="outlined"
                  disabled={
                    !toRelate ||
                    toRelate === props.documentId ||
                    relatedDocuments.filter((rd) => rd.documentID === toRelate)
                      .length > 0
                  }
                  color="default"
                  startIcon={<Link />}
                  onClick={relateDocument}
                  loading={saving}
                >
                  {t("related_document_link_button")}
                </ActionButton>
              </div>
            </div>
            <Divider className={classes.divider} />
            <div className={classes.relatedContainer}>
              {loading ? (
                <>
                  {Array.from(Array(5)).map(() => {
                    return (
                      <div style={{ marginTop: 4, marginBottom: 12 }}>
                        <Skeleton
                          variant="rect"
                          style={{ width: "35%", marginBottom: 5 }}
                          height={25}
                          animation="wave"
                        />
                        <Skeleton
                          variant="rect"
                          style={{ width: "90%" }}
                          height={25}
                          animation="wave"
                        />
                      </div>
                    );
                  })}
                </>
              ) : (
                <>
                  {relatedDocuments.length === 0 ? (
                    <Typography style={{ marginTop: 15, textAlign: "center" }}>
                      {error
                        ? t("related_document_error_occured")
                        : t("related_document_no_related")}
                    </Typography>
                  ) : (
                    <AutoSizer>
                      {({ height, width }) => (
                        <Virtuoso
                          overscan={10}
                          totalCount={relatedDocuments.length}
                          itemContent={(idx) => renderItem(idx)}
                          defaultItemHeight={63}
                          style={{ width: width, height: height }}
                        />
                      )}
                    </AutoSizer>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </>
      )}
    </>
  );
}

export default SidebarRelatedDocuments;
